// src/features/business-functions/pages/BusinessFunctionAccountPage.jsx
import React, { useEffect } from 'react';
import { Box, Fade } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import DynamicBreadcrumbs from '../../../components/Shared/Breadcrumb/index.tsx';
import { useSnackBar } from '../../../components/FlowBuilder/Common/SnackBar/index.tsx';
import { createBreadcrumbNavigateHandler } from '../../../config/navigation/breadcrumbs';
import { ROUTES } from '../../../constants/routes';
import { FeatureThemeProvider } from '../../../theme/FeatureThemeProvider';
import { userInformationAtom } from '../../../Services/_states';
import useBusinessFunctionsSx from '../hooks/useBusinessFunctionsSx';
import useAccountDetail from '../hooks/useAccountDetail';
import useAccountWorkspaces from '../hooks/useAccountWorkspaces';
import useAccountTabs from '../hooks/useAccountTabs';
import useAccountBreadcrumbs from '../hooks/useAccountBreadcrumbs';
import AccountHeader from '../components/AccountHeader';
import AccountTabBar from '../components/AccountTabBar';
import WorkspacesTab from '../components/workspaces/WorkspacesTab';
import AccountDetailsTab from '../components/details/AccountDetailsTab';
import { ACCOUNT_STATUS_COPY } from '../constants/accountConstants';

/**
 * Smart Controller Page — connects all hooks to presentation components.
 * All business logic is delegated to hooks; all visuals to components.
 * This page only wires them together.
 */
function BusinessFunctionAccountPageContent() {
  const styles = useBusinessFunctionsSx();
  const navigate = useNavigate();
  const { showSnackBar } = useSnackBar();
  const userInfo = useRecoilValue(userInformationAtom);

  const organisationId =
    userInfo?.profile?.organisation?.organisation_id
    || userInfo?.profile?.organisation_id
    || '';

  // ── Hooks ─────────────────────────────────────────────────────────
  const {
    accountId,
    account,
    isLoading,
    isForbidden,
  } = useAccountDetail();

  const {
    workspaces,
    isLoading: isLoadingWorkspaces,
    error: workspacesError,
    refetch: refetchWorkspaces,
  } = useAccountWorkspaces(isForbidden ? '' : accountId);

  const { activeTab, tabConfig, handleTabChange } = useAccountTabs();

  const breadcrumbsData = useAccountBreadcrumbs(account?.name);

  useEffect(() => {
    if (!isForbidden) return;
    showSnackBar(ACCOUNT_STATUS_COPY.unavailable, 'warning');
    navigate(ROUTES.CAMPAIGN.BUSINESS_FUNCTIONS, { replace: true });
  }, [isForbidden, navigate, showSnackBar]);

  // ── Tab content rendering ─────────────────────────────────────────
  const renderTabContent = () => {
    switch (activeTab) {
      case 'workspaces':
        return (
          <WorkspacesTab
            accountId={accountId}
            accountName={account?.name}
            organisationId={account?.organisationId || organisationId}
            workspaces={workspaces}
            isLoading={isLoadingWorkspaces}
            error={workspacesError}
            styles={styles}
            onWorkspaceCreated={refetchWorkspaces}
            onWorkspacesChange={refetchWorkspaces}
          />
        );
      case 'details':
        return (
          <AccountDetailsTab
            account={account}
            isLoading={isLoading}
            styles={styles}
          />
        );
      default:
        return null;
    }
  };

  if (isForbidden) {
    return null;
  }

  return (
    <Box sx={styles.pageRoot}>
      <DynamicBreadcrumbs
        items={breadcrumbsData}
        onBreadcrumbClick={createBreadcrumbNavigateHandler(navigate)}
      />

      <AccountHeader
        account={account}
        styles={styles}
        isLoading={isLoading}
      />

      <AccountTabBar
        tabConfig={tabConfig}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        styles={styles}
      />

      <Box sx={styles.tabPanelContainer}>
        <Fade key={activeTab} in timeout={250}>
          <Box>
            {renderTabContent()}
          </Box>
        </Fade>
      </Box>
    </Box>
  );
}

/**
 * Entry point — wraps content in FeatureThemeProvider for scoped theme injection,
 * exactly like IntervenePage and LicenseDashboard.
 */
export default function BusinessFunctionAccountPage() {
  return (
    <FeatureThemeProvider>
      <BusinessFunctionAccountPageContent />
    </FeatureThemeProvider>
  );
}
